import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import styles from './ReminderCategoriesDetails.module.scss';
import InputError from 'common/components/inputError/InputError';
import Input from 'common/components/input/Input';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import { useForm } from 'react-hook-form';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE, removeAccents } from 'Config';
import GreyArrow from 'assets/svg/Arrow-grey.svg';
import { useEffect, useState } from 'react';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import React from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { ReminderCategoriesDto } from 'api/reminderCategories/models/ReminderCategoriesDto';
import ReminderCategoriesService from 'api/reminderCategories/ReminderCategoriesService';
import { useHistory, useParams } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import SelectController from 'common/components/select/SelectController';

type Props = {
};

const ReminderCategoriesDetails: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const history = useHistory();
    // eslint-disable-next-line
    let { type, itemId } = useParams<{ type: string, itemId: string }>();
    const [reminderCategory, setReminderCategory] = useState<ReminderCategoriesDto | null>(null);

    const [itemToRemove, setItemToRemove] = useState<ReminderCategoriesDto>();
    const [dialogDeleteItemIsOpen, setDialogDeleteItemIsOpen] = React.useState(false);

    const form = useForm<ReminderCategoriesDto>({ shouldUnregister: false });
    const { register, handleSubmit, setValue, reset, errors } = form;

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasReminderCategoriesWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_REMINDER_CATEGORIES_WRITE']);

    const [typeList, setTypeList] = useState<SelectValueLabel[]>([]);


    const onSubmit = async (formData: ReminderCategoriesDto) => {
        try {
            if (!hasReminderCategoriesWritePolicy) { return; }

            Loading.show();

            if (reminderCategory != null) {
                reminderCategory.designation = formData.designation;
                reminderCategory.type = formData.type; 
                await ReminderCategoriesService.update(reminderCategory);
            } else {
                itemId = await ReminderCategoriesService.create(formData);
                getData();
            }

            Loading.hide();
            addToast(t('common.messages.record_save_success'), {
                appearance: 'success',
            });

            history.goBack();
        } catch (error) {
            Loading.hide();

            if (error?.response?.status === 409) {
                addToast(t('common.messages.name_already_exists'), { appearance: 'warning' });
                return;
            }

            addToast(t('common.messages.record_save_error'), {
                appearance: 'error',
            });

            Logger.error(
                LOGGER_LOG_TYPE.REQUEST,
                `Couldn't create or update reminder categories`,
                error
            );
        }
    };

    const getData = async () => {

        const types = [{ value: 'VEHICLE', label: t('reminder_categories.types.VEHICLE') }]
        if (itemId) {
            try {
                Loading.show();

                const result = await ReminderCategoriesService.getById(itemId);
                if(result && result.isSystem){
                    result.designation = t('reminder_categories.category_types.' + result.designation as any);
                }
                if (result?.type === 'USER' && !types.find(x => x.value === 'USER')) {
                    types.unshift({ value: 'USER', label: t('reminder_categories.types.USER') });
                }
                setTypeList(types);
                setReminderCategory(result);
                reset(result);

                Loading.hide();
            }
            catch (error) {
                addToast(t('common.messages.error_load_info'), {
                    appearance: 'error',
                });

                Logger.error(
                    LOGGER_LOG_TYPE.REQUEST,
                    `Couldn't get reminder categories`,
                    error
                );
                Loading.hide();
            }
        }
        else{
            setTypeList(types);
            setValue('type',types[0].value);
        }
    }

    const showRemoveItemDialog = (item: ReminderCategoriesDto) => {
        setItemToRemove(item);
        setDialogDeleteItemIsOpen(true);
    };

    const removeItem = async () => {
        setDialogDeleteItemIsOpen(false);
        if (!!itemToRemove) {
            try {
                Loading.show();
                await ReminderCategoriesService.remove(itemToRemove);
                addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
                history.push(`/settings/reminderCategories/list`);
                Loading.hide();
            } catch (error) {
                addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
                Loading.hide();
            }
        }
    };

    useEffect(() => {
        getData();
    }, []);


    const onGoBack = () => {
        if(reminderCategory){
            reset(reminderCategory);
        }
        history.goBack();
    }

    const isDanger = form.watch('isDanger');

    return (
        <div>
            <div className={styles.pageNameContainer}>
                <div className={styles.pageNameContent} onClick={() => history.push(`/settings/reminderCategories/list`)} >
                    <img className={styles.pageNameContentImage} src={GreyArrow} />
                    <span className={styles.pageNameContentText}>
                        {t('settings.reminder_categories')}
                    </span>
                </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>

                 <Row>
                     <Col xs={12} md={6} lg={6} className={styles.col}>
                            <FormItem>
                                <Label className={styles.label}>{t('reminder_categories.type')} {type !== 'details' ? '*' : ''}</Label>
                                <SelectController
                                    form={form}
                                    name='type'
                                    menuPortalTarget={document.querySelector('body')}
                                    options={typeList}
                                    placeholder={t('reminder_categories.type')}
                                    rules={{ required: true }}
                                    isDisabled={type === 'details'}
                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                    onChangeSelect={(data: SelectValueLabel) => {
                                        setValue('type', data ? data.value : null);
                                    }}

                                />
                                <InputError error={errors.type} />
                            </FormItem>
                        </Col>
                </Row>

                <Row>
                    <Col xs={12} sm={6}>
                        <FormItem>
                            <Label className={styles.label}>
                                {t('reminder_categories.designation')} {type !== 'details' ? '*' : ''}
                            </Label>
                            <Input
                                name='designation'
                                placeholder={t('reminder_categories.designation')}
                                defaultValue={reminderCategory?.designation}
                                ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                                disabled={type === 'details'} 
                            />
                            <InputError error={errors.designation} maxLength={250} />
                        </FormItem>
                    </Col>
                    </Row>
                   

                <div className={styles.buttonContainer}>
                    <Button preset={'secondary'}
                        type='button'
                        text={t('common.cancel')}
                        onClick={() => onGoBack()} />
                    {type === 'details' && hasReminderCategoriesWritePolicy &&
                        <Button
                            type="button"
                            text={t('common.remove')}
                            preset={'danger'}
                            disabled={reminderCategory?.isSystem}
                            disabledMessage={t('reminder_categories.forbidden_tooltip')}
                            onClick={() => showRemoveItemDialog({ id: reminderCategory?.id } as ReminderCategoriesDto)} />
                    }
                    {type === 'details' && hasReminderCategoriesWritePolicy && <Button type='button' 
                        disabled={reminderCategory?.isSystem} text={t('common.edit')} 
                        disabledMessage={t('reminder_categories.forbidden_tooltip')} 
                        onClick={() => history.push(`/settings/reminderCategories/edit/${itemId}`)} />}
                    {type !== 'details' && hasReminderCategoriesWritePolicy && <Button type='submit' text={t('common.save')} />}

                    <QuestionYesNo message={t('common.messages.remove_reminder_category', { category: reminderCategory?.designation ?? '' })}
                        isVisible={dialogDeleteItemIsOpen}
                        onYes={() => removeItem()}
                        onNo={() => setDialogDeleteItemIsOpen(false)} />
                </div>
            </form>
        </div>
    );
};

export default ReminderCategoriesDetails;
